import {
  SiteInfo,
  BoundingPosition,
  LoginProvider,
  ScaleMode,
  RoomCount,
  MessageHistory,
  UserProperties,
  QuestionnarieResult,
  SiteStatus,
  Campaign,
  RoomActivityLogs,
  MembersRegistration,
  LogRequest,
  MemberRegistrationFailiureResult as MemberRegistrationFailureResult,
} from './../utils/models';
import { io, Manager, Socket } from 'socket.io-client';
import {
  CoordinatesPosition,
  GetScheduleResponse,
  GuestPermission,
  LoginInfo,
  Room,
  RoomObject,
  RoomSpace,
  SiteMember,
  SubRoom,
  UpdateableUserRoomState,
  UploadFileRequest,
  UploadFileType,
  User,
  UserRoomState,
  UsersWithRights,
} from '@/utils/models';
import EventEmitter from 'eventemitter3';

export class DataService {
  private socketRef!: Socket;
  private manager!: Manager;
  readonly url: string;
  private isConnect: boolean = false;
  private event = new EventEmitter();

  get socketId() {
    return this.socketRef.id;
  }

  constructor(
    serverUrl: string,
    private readonly loginInfo: LoginInfo,
    private readonly version: string,
    private readonly versionText: string,
  ) {
    this.url = serverUrl;
    this.createConnection();
    this.registerDefaultListener();
    this.registerListeners();
  }
  removeAllListeners() {
    this.socketRef.removeAllListeners();
    /// event は、app で設定されるListenerがあるので、reconnect だけを削除
    this.event.removeListener('reconnect');
    // 削除されちゃうリスナーを再登録
    this.registerListeners();
  }
  private registerDefaultListener() {
    this.manager.on('reconnect', (attempt) => {
      this.emitLogin(true).then((u) => {
        if (!u) {
          this.event.emit('reconnect login failed', 'token timed out.');
        } else {
          this.event.emit('reconnect');
        }
      });
    });
    this.manager.on('reconnect_error', (err) => {
      console.log('reconnect error', err);
    });
    this.manager.on('reconnect_failed', () => {
      console.log('reconnect failed');
      this.event.emit('reconnect failed');
    });
    this.manager.on('close', (reason, desc) => {
      console.log('close by peer', reason, desc);
    });
  }
  private registerListeners() {
    this.onConnected(() => console.log('connected', this.socketRef.id));
    this.socketRef.on('connect_error', (error) => {
      if (error.message.startsWith('invalid')) {
        this.event.emit('reconnect login failed', error.message);
      }
    });
    this.socketRef.on('invalid version', (version: string) => {
      this.event.emit('invalid version', version);
    });
    this.socketRef.on('updated user properties', (properties: UserProperties) => {
      this.event.emit('updated user properties', properties);
    });
  }
  public onReconnectFailed(callback: (message: string) => void) {
    this.event.on('reconnect login failed', (message: string) => {
      callback(message);
    });
    this.event.on('reconnect failed', () => {
      callback('reconnect failed');
    });
  }
  public onVersionInvalid(callback: (version: string) => void) {
    this.event.on('invalid version', (version: string) => {
      callback(version);
    });
  }
  // ------------- CONNECT --------------

  private createConnection(): void {
    console.log('connect to data server: ', this.url);
    this.manager = new Manager(this.url, {
      path: '/zawa-data/socket.io/',
      transports: ['websocket', 'polling'],
    });
    this.socketRef = this.manager.socket('/', {
      auth: { id: this.loginInfo.id, tokenHash: this.loginInfo.tokenHash },
    });
    this.connect();
  }
  public connect() {
    if (this.socketRef && !this.isConnect) {
      this.isConnect = true;
      this.socketRef.connect();
    }
  }

  public getConnectionStatus(): boolean {
    return this.socketRef.connected;
  }

  // ------------- DISCONNECT --------------

  public disconnect(): void {
    console.log('disconnect from data server:', this.url);
    this.isConnect = false;
    this.socketRef.disconnect();
  }

  // ------------- LISTEN -----------------

  public onConnected(callback: () => void): void {
    if (this.socketRef.connected) {
      callback();
    }
    this.socketRef.on('connect', () => {
      callback();
    });
  }
  public onceConnected(callback: () => void): void {
    if (this.socketRef.connected) {
      callback();
    }
    this.socketRef.once('connect', () => {
      callback();
    });
  }
  public onReconnected(callback: () => void): void {
    this.event.on('reconnect', () => {
      callback();
    });
  }

  public onRemoveError(callback: (message: string, type: 'room' | 'space') => void): void {
    this.socketRef.on('remove error', (payload: { message: string; type: 'room' | 'space' }) => {
      callback(payload.message, payload.type);
    });
  }

  public onDuplicateSpaceRoom(callback: (roomOrSpace: 'room' | 'space') => void): void {
    this.socketRef.on('duplicate space room', (roomOrSpace: 'room' | 'space') => {
      callback(roomOrSpace);
    });
  }

  public onUpdatedUserProperties(callback: (properties: UserProperties) => void) {
    this.event.on('updated user properties', (properties: UserProperties) => {
      callback(properties);
      console.log('callbacked', properties);
    });
  }

  // ------------- EMIT -----------------

  public emitAddRoom(
    spaceId: string,
    roomName: string,
    guestPermission: GuestPermission,
    allowUserEmails: string[] | undefined,
    callback: (error?: string) => void,
  ): void {
    this.socketRef.emit(
      'register room',
      {
        spaceId: spaceId,
        roomName,
        guestPermission: guestPermission,
        allowUserEmails,
      },
      callback,
    );
  }

  public emitRemoveRoom(spaceId: string, roomId: string): void {
    this.socketRef.emit('remove room', { spaceId: spaceId, roomId: roomId });
  }

  public emitEditRoom(
    spaceId: string,
    toSpaceId: string,
    roomId: string,
    toRoomName: string,
    toDescription: string,
    toPermission: GuestPermission,
    toEditRoomSize: { width: number; height: number },
    toSubRooms: SubRoom[],
    allowDomains: string[],
    allowUserEmails: string[],
    adminEmails: string[],
    initialScaleMode: ScaleMode,
    initialScale: number | undefined,
    minScaleMode: ScaleMode,
    minScale: number | undefined,
    isPublicRoom: boolean,
    defaultVoiceArea?: number,
    enteringSoundVolume?: number,
    doorSoundVolume?: number,
    toImageData?: string,
    screenShareScope?: Room['screenShareScope'],
    enableScreenShareIdling?: Room['enableScreenShareIdling'],
    callback?: (error: string) => void,
  ): void {
    this.socketRef.emit(
      'update room',
      {
        spaceId,
        roomId,
        toSpaceId,
        toRoomName,
        toDescription,
        toPermission,
        toEditRoomSize,
        toSubRooms,
        toImageData,
        isPublicRoom,
        initialScaleMode,
        initialScale,
        minScaleMode,
        minScale,
        allowDomains,
        allowUserEmails,
        adminEmails,
        defaultVoiceArea,
        enteringSoundVolume,
        doorSoundVolume,
        screenShareScope,
        enableScreenShareIdling,
      },
      callback,
    );
  }

  public emitSetAvatar(avatar: string, avatarShape: 'circle' | 'rectangle'): void {
    this.socketRef.emit('upload avatar', { imageBase64: avatar, avatarShape });
  }

  public emitRemoveAvatar(): void {
    this.socketRef.emit('remove avatar');
  }
  public emitDisplayName(displayName: string) {
    this.socketRef.emit('update displayName', displayName);
  }
  public emitVoiceAreaColor(voiceAreaColor: string): void {
    this.socketRef.emit('update voiceAreaColor', voiceAreaColor);
  }
  public emitGetRoomState(
    spaceName: string,
    roomName: string,
    withUserState?: boolean,
  ): Promise<RoomSpace | undefined> {
    return new Promise((resolv, reject) => {
      this.socketRef.emit('get room state', { spaceName, roomName, withUserState }, (space?: RoomSpace) => {
        resolv(space);
      });
    });
  }
  public emitGetRoomStateById(
    spaceId: string,
    roomId: string,
    withUserState?: boolean,
  ): Promise<RoomSpace | undefined> {
    return new Promise((resolv, reject) => {
      this.socketRef.emit('get room state by id', { spaceId, roomId, withUserState }, (space?: RoomSpace) => {
        resolv(space);
      });
    });
  }
  // ------------- LISTEN --------------

  public onSpacesStateChanged(callback: (spacesState: RoomSpace[]) => void): void {
    this.socketRef.on('update spaces state', (spacesState: RoomSpace[]) => callback(spacesState));
  }

  public onClearRoomStateCache(callback: (roomId: string) => void): void {
    this.socketRef.on('clear room state cache', (roomId: string | 'reset') => callback(roomId));
  }

  // ------------- EMIT --------------
  public emitLogin(reconnect: boolean = false): Promise<{ user: User; properties: UserProperties } | string> {
    const { authMode, name } = this.loginInfo;
    const r = new Promise<{ user: User; properties: UserProperties }>((resolv, reject) => {
      this.socketRef.emit(
        'emit login',
        {
          ...this.loginInfo,
          version: this.version,
          versionText: this.versionText,
        },
        reconnect,
        (res: { user: User; properties: UserProperties }) => {
          resolv(res);
        },
      );
    });
    return r;
  }

  public emitRegisterOnWelcomePage(): void {
    this.socketRef.emit('register on welcome page');
  }

  public emitLeaveFromWelcomePage(): void {
    this.socketRef.emit('leave from welcome page');
  }

  public emitAddSpace(
    spaceName: string,
    isAllowGuest: boolean,
    organizations: string[],
    spaceType: RoomSpace['spaceType'],
    callback: (error?: string) => void,
  ): void {
    this.socketRef.emit(
      'register space',
      {
        spaceName: spaceName,
        isAllowGuest,
        organizations,
        spaceType,
      },
      callback,
    );
  }

  public emitRemoveSpace(spaceId: string): void {
    this.socketRef.emit('remove space', spaceId);
  }

  public emitEditSpace(
    spaceId: string,
    spaceName: string,
    isAllowGuest: boolean,
    organizations: string[],
    spaceType: RoomSpace['spaceType'],
    callback: (error?: string) => void,
  ): void {
    this.socketRef.emit(
      'update space',
      {
        spaceId,
        spaceName,
        isAllowGuest,
        organizations,
        spaceType,
      },
      callback,
    );
  }
  public emitGetSpaces(callback: (spaces: RoomSpace[]) => void): void {
    this.socketRef.emit('get spaces', callback);
  }

  // ------------- LISTEN --------------

  public onSpaceStateChanged(callback: (spaceState: RoomSpace) => void): void {
    this.socketRef.on('update space state', (spaceState: RoomSpace) => {
      callback(spaceState);
    });
  }

  public onLocalUserJoinedSubRoom(callback: (args: RoomObject[]) => void): void {
    this.socketRef.on('local user joined subroom', (args: RoomObject[]) => callback(args));
  }
  public onJoinedSubroom(callback: (args: { roomId: string; subId: string; userId: string }) => void): void {
    this.socketRef.on('joined subroom', (args: { roomId: string; subId: string; userId: string }) => callback(args));
  }
  public onUsersState(callback: (userJoinedNotification: boolean, room: Room, joinUserId?: string) => void): void {
    this.socketRef.on(
      'users state',
      (payload: { userJoinedNotification: boolean; room: Room; joinUserId?: string }) => {
        callback(payload.userJoinedNotification, payload.room, payload.joinUserId);
      },
    );
  }
  public onRemoveRemoteUser(callback: (userIdInConference: string) => void): void {
    this.socketRef.on('remove remote user', (userIdInConference: string) => callback(userIdInConference));
  }
  public onVideoMuted(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'videoMuted' ? callback(res.state) : null,
    );
  }
  public onAudioMuted(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'audioMuted' ? callback(res.state) : null,
    );
  }
  public onSpeakerMuted(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'speakerMuted' ? callback(res.state) : null,
    );
  }
  public onVoiceArea(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'voiceArea' ? callback(res.state) : null,
    );
  }
  public onStatus(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'status' ? callback(res.state) : null,
    );
  }
  public onPosition(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'position' ? callback(res.state) : null,
    );
  }
  public onMessage(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'userMessage' ? callback(res.state) : null,
    );
  }
  public onWindowFocusChanged(callback: (userMetadata: UserRoomState) => void): void {
    this.socketRef.on(
      'user state updated',
      (res: { propertyName: keyof UpdateableUserRoomState; state: UserRoomState }) =>
        res.propertyName === 'isFocus' ? callback(res.state) : null,
    );
  }

  public onUpdateUserProps(callback: (payload: Partial<User>) => void): void {
    this.socketRef.on('update user props', (payload: Partial<User>) => callback(payload));
  }

  public onRenderImage(callback: (payload: RoomObject) => void): void {
    this.socketRef.on('render image', (payload: RoomObject) => callback(payload));
  }
  public onChangeImagePosition(callback: (payload: RoomObject) => void): void {
    this.socketRef.on('change image position', (payload: RoomObject) => callback(payload));
  }
  public onUpdateObjectSize(callback: (payload: RoomObject) => void): void {
    this.socketRef.on('update object size', (payload: RoomObject) => callback(payload));
  }
  public onShareScreenStarted(callback: (payload: RoomObject) => void): void {
    this.socketRef.on('share screen started', (payload: RoomObject) => callback(payload));
  }
  public onShareScreenStoped(callback: (payload: RoomObject) => void): void {
    this.socketRef.on('share screen stoped', (payload: RoomObject) => callback(payload));
  }
  public onChangeShareScreenVideoPosition(callback: (arg: RoomObject) => void): void {
    this.socketRef.on('change share screen video position', (arg: RoomObject) => callback(arg));
  }
  public onUpdateShareScreenVideoSize(callback: (arg: RoomObject) => void): void {
    this.socketRef.on('update share screen video size', (arg: RoomObject) => callback(arg));
  }
  public onUploadedImagesState(callback: (payload: Array<RoomObject>) => void): void {
    this.socketRef.on('uploaded images state', (payload: Array<RoomObject>) => callback(payload));
  }
  public onImageDeleted(callback: (image: RoomObject) => void): void {
    this.socketRef.on('image deleted', (image: RoomObject) => callback(image));
  }
  public onRegisterBoard(callback: (image: RoomObject) => void): void {
    this.socketRef.on('board registered', (image: RoomObject) => callback(image));
  }
  public onUpdateBoard(callback: (image: RoomObject) => void): void {
    this.socketRef.on('board updated', (image: RoomObject) => callback(image));
  }
  public onDeleteBoard(callback: (image: RoomObject) => void): void {
    this.socketRef.on('board deleted', (image: RoomObject) => callback(image));
  }
  public onOpenTeamsRequest(callback: (subId: string, userId: string) => void): void {
    this.socketRef.on('open teams request', (subId: string, userId: string) => callback(subId, userId));
  }
  public onBellRang(callback: () => void): void {
    this.socketRef.on('bell rung', () => callback());
  }
  public onKnockByUser(callback: (args: { fromUserId: string; toUserId: string }) => void): void {
    this.socketRef.on('knocked by user', (args: { fromUserId: string; toUserId: string }) => callback(args));
  }

  public onResponsedByKnockUser(callback: (args: { message: string }) => void): void {
    this.socketRef.on('responsed by knock user', (args: { message: string }) => callback(args));
  }

  public onJoinAnotherBrowser(callback: () => void): void {
    this.socketRef.on('join another browser', () => callback());
  }

  public onJoinRequestSent(callback: (fromUserId: string) => void): void {
    this.socketRef.on('join request sent', (fromUserId: string) => callback(fromUserId));
  }

  public onAllowedJoinRoom(callback: (args: { userId: string }) => void): void {
    this.socketRef.on('allowed join room', (args: { userId: string }) => callback(args));
  }

  public onResponseGetSchedule(callback: (res: GetScheduleResponse | undefined) => void): void {
    this.socketRef.on('response schedule', (res: GetScheduleResponse | undefined) => callback(res));
  }

  public onRoomObjectUpdated(callback: (res: RoomObject) => void): void {
    this.socketRef.on('roomobject updated', (res: RoomObject) => callback(res));
  }
  public onAudioLevelChanged(callback: (res: { userId: string; audioLevel: number }) => void): void {
    this.socketRef.on('audioLevel', (res: { userId: string; audioLevel: number }) => callback(res));
  }
  public onChangeMyFavoriteRooms(callback: (roomIds: string[]) => void): void {
    this.socketRef.on('my favorite rooms', (roomIds: string[]) => callback(roomIds));
  }
  public onUpdateRoomCount(callback: (roomCount: RoomCount) => void): void {
    this.socketRef.on('update room count', (roomCount: RoomCount) => callback(roomCount));
  }
  public onUpdateMessageHistories(callback: (messageHistories: MessageHistory[]) => void): void {
    this.socketRef.on('update message histories', (messageHistories: MessageHistory[]) => callback(messageHistories));
  }
  public onRoomUpdated(callback: (res: { room: Room; isNeedReload: boolean }) => void): void {
    this.socketRef.on('room updated', (res: { room: Room; isNeedReload: boolean }) => callback(res));
  }
  public onSiteStatusChange(callback: (status: SiteStatus, senderId?: string) => void) {
    this.socketRef.on('site status change', (status: SiteStatus, senderId?: string) => callback(status, senderId));
  }

  public onSiteInfoUpdated(callback: (siteInfo: SiteInfo) => void) {
    this.socketRef.on('site info updated', (info: SiteInfo) => {
      callback(info);
    });
  }
  public onCampaignsUpdated(callback: (campaigns: Campaign[]) => void) {
    this.socketRef.on('update campaigns', (campaigns: Campaign[]) => {
      callback(campaigns);
    });
  }

  public onCampaignUpdated(callback: (campaign: Campaign) => void) {
    this.socketRef.on('update campaign', (campaign: Campaign) => {
      callback(campaign);
    });
  }

  public onCampaignCreated(callback: (campaign: Campaign) => void) {
    this.socketRef.on('created campaign', (campaign: Campaign) => {
      callback(campaign);
    });
  }

  public onCampaignsDeleted(callback: (campaigns: Campaign[]) => void) {
    this.socketRef.on('deleted campaigns', (campaigns: Campaign[]) => {
      callback(campaigns);
    });
  }

  public onRoomLogsUpdated(callback: (logs: RoomActivityLogs) => void): void {
    this.socketRef.on('room logs updated', (logs: RoomActivityLogs) => callback(logs));
  }

  public onMemberRegistrationFailed(callback: (failureResults: MemberRegistrationFailureResult[]) => void) {
    return this.socketRef.on('member registration failed', (failureResults: MemberRegistrationFailureResult[]) =>
      callback(failureResults),
    );
  }

  public onActiveShareScreenUsersChange(callback: (ids: string[]) => void): void {
    this.socketRef.on('active share screen users change', (ids: string[]) => callback(ids));
  }

  public onErrorFeedback(callback: (message: string) => void): void {
    this.socketRef.on('error feedback', (message: string) => callback(message));
  }

  // ------------- EMIT --------------
  public emitJoinRoom(
    spaceId: string,
    roomId: string,
    audioMuted: boolean,
    videoMuted: boolean,
    reconnect?: boolean,
    position?: CoordinatesPosition,
  ): Promise<any> {
    return new Promise<void>((resolv) => {
      this.socketRef.emit(
        'join room',
        {
          spaceId,
          roomId,
          videoMuted,
          audioMuted,
          reconnect,
          position,
        },
        () => {
          resolv();
        },
      );
    });
  }
  public emitLeaveRoom(): void {
    this.socketRef.emit('leave room');
  }
  public emitJoinSubRoom(subId: string): void {
    this.socketRef.emit('join subroom', subId);
  }
  public emitLeaveSubRoom(roomId: string, subId: string, userId: string): void {
    this.socketRef.emit('leave subroom', {
      roomId,
      subId,
      userId,
    });
  }
  public emitUploadImage(imageBase64: string, width: number, height: number, subId: string): void {
    this.socketRef.emit('upload image', {
      imageBase64: imageBase64,
      width: width,
      height: height,
      subId,
    });
  }

  public emitImagePositionChanged(id: string, position: CoordinatesPosition, subId: string, userId: string): void {
    this.socketRef.emit('image position changed', {
      id: id,
      position: position,
      subId,
      userId,
    });
  }
  public emitNewObjectSize(id: string, newSize: BoundingPosition): void {
    this.socketRef.emit('new object size', { id: id, size: newSize });
  }

  public emitScreenShareVideoPositionChanged(
    id: string,
    position: CoordinatesPosition,
    subId: string,
    userId: string,
  ): void {
    this.socketRef.emit('share screen video position changed', {
      id,
      position,
      subId,
      userId,
    });
  }
  public emitNewScreenShareVideoSize(id: string, newSize: BoundingPosition): void {
    this.socketRef.emit('new share screen video size', { id, size: newSize });
  }
  public emitDeleteImage(id: string): void {
    this.socketRef.emit('delete image', id);
  }
  public emitVideoMuted(isMute: boolean): void {
    this.emitUserState('videoMuted', { videoMuted: isMute });
  }
  public emitAudioMuted(isMute: boolean): void {
    this.emitUserState('audioMuted', { audioMuted: isMute });
  }
  public emitSpeakerMuted(speakerMuted: boolean): void {
    this.emitUserState('speakerMuted', { speakerMuted: speakerMuted });
  }
  public emitVoiceArea(voiceArea: number): void {
    this.emitUserState('voiceArea', { voiceArea: voiceArea });
  }
  public emitStatus(status: string): void {
    this.emitUserState('status', { status: status });
  }
  public emitPosition(position: CoordinatesPosition): void {
    this.emitUserState('position', { position: position });
  }
  public emitMessage(userMessageDisplayed: string, reconnect?: boolean): void {
    this.emitUserState('userMessage', { userMessage: userMessageDisplayed }, reconnect);
  }

  public emitWindowFocus() {
    this.emitUserState('isFocus', { isFocus: true });
  }

  public emitWindowBlur() {
    this.emitUserState('isFocus', { isFocus: false });
  }

  private emitUserState(
    propertyName: keyof UpdateableUserRoomState,
    newState: Partial<UpdateableUserRoomState>,
    reconnect?: boolean,
  ) {
    this.socketRef.emit('update user state', {
      propertyName,
      newState,
      reconnect,
    });
  }
  public emitStartShareScreen(subId: string, channelId: string): void {
    this.socketRef.emit('start share screen', { subId, channelId });
  }
  public emitOpenTeams(subId: string, userId: string) {
    this.socketRef.emit('open teams', { subId, userId });
  }
  public emitStopShareScreen(channelId?: string): void {
    this.socketRef.emit('stop share screen', { channelId });
  }
  public emitRegisterMyList(userId: string) {
    return new Promise<User>((resolv) => {
      this.socketRef.once('reply register my list', (user: User) => {
        resolv(user);
      });
      this.socketRef.emit('register my list', userId);
    });
  }
  public emitRemoveMyList(userId: string) {
    return new Promise<User>((resolv) => {
      this.socketRef.once('reply remove my list', (user: User) => {
        resolv(user);
      });
      this.socketRef.emit('remove my list', userId);
    });
  }
  public emitDraw(start: CoordinatesPosition, end: CoordinatesPosition, color: string, strokeWeight: number): void {
    this.socketRef.emit('draw', {
      type: 'draw',
      coordinates: {
        start: start,
        end: end,
      },
      strokeColor: color,
      strokeWeight: strokeWeight,
    });
  }

  public emitErase(position: CoordinatesPosition): void {
    this.socketRef.emit('erase', {
      type: 'erase',
      coordinates: position,
    });
  }

  public emitBellRing(): void {
    this.socketRef.emit('ring bell');
  }

  public emitKnock(toUserId: string) {
    this.socketRef.emit('knock', { toUserId });
  }

  public emitResponsedByKnockUser(toUserId: string, message: string) {
    this.socketRef.emit('responsed by knock', { toUserId, message });
  }

  public emitAllowGuestUser(userId: string) {
    this.socketRef.emit('allow guest user', userId);
  }

  public emitRegisterBoard(payload: RoomObject) {
    this.socketRef.emit('register board', payload);
  }
  public emitUpdateBoard(payload: RoomObject) {
    this.socketRef.emit('update board', payload);
  }

  public emitRequestSchedule(targetUserId: string) {
    this.socketRef.emit('get schedule', targetUserId);
  }

  public emitRequestLock(tartetObjectId: string): void {
    this.socketRef.emit('lock roomobject', tartetObjectId);
  }
  public emitRequestUnlock(tartetObjectId: string): void {
    this.socketRef.emit('unlock roomobject', tartetObjectId);
  }

  public emitGetLog(spacesIds: string[], range: Date[]): Promise<{ path: string; filename: string }> {
    return new Promise((resolv, reject) => {
      this.socketRef.once('logs file generated', (path: string, filename: string) => {
        resolv({ path, filename });
      });
      this.socketRef.emit('request logs', ',', spacesIds, range);
    });
  }

  public emitGetQuestionnaireLog(logRequest: LogRequest): Promise<{ path: string; filename: string }> {
    return new Promise((resolv, reject) => {
      this.socketRef.once('questionnaire file generated', (path: string, filename: string) => {
        resolv({ path, filename });
      });
      this.socketRef.emit('request questionnaire csv', logRequest);
    });
  }

  public emitRoomGetLog(roomId: string, range: Date[]): Promise<{ path: string; filename: string }> {
    return new Promise((resolv, reject) => {
      this.socketRef.once('logs file generated', (path: string, filename: string) => {
        resolv({ path, filename });
      });
      this.socketRef.emit('request room logs', ',', roomId, range);
    });
  }
  public emitGetUsers(): Promise<UsersWithRights> {
    return new Promise((resolv, reject) => {
      this.socketRef.emit('request user list', (response: UsersWithRights) => {
        resolv(response);
      });
    });
  }
  public emitRequestUpload(fileType: UploadFileType): Promise<UploadFileRequest> {
    return new Promise((resolv, reject) => {
      this.socketRef.once('upload file requested', (request: UploadFileRequest) => {
        resolv(request);
      });
      this.socketRef.emit('upload file request', fileType);
    });
  }
  public emitAudioLevel(audioLevel: number) {
    this.socketRef.emit('audioLevel', audioLevel);
  }
  public emitUpdateSiteInfo(siteInfo: SiteInfo) {
    this.socketRef.emit('update site info', siteInfo);
  }
  public emitRegisterMembers(members: MembersRegistration) {
    return new Promise<SiteMember[]>((resolv) => {
      this.socketRef.emit('register members', members, (res: SiteMember[]) => resolv(res));
    });
  }
  public emitRemoveMembers(member: SiteMember[]) {
    this.socketRef.emit('remove members', member);
  }
  public emitRegisterSiteAdeminRole(members: SiteMember[]) {
    return new Promise<User[]>((resolv) => {
      this.socketRef.emit('register site admin role', members, (res: User[]) => resolv(res));
    });
  }
  public emitRemoveSiteAdeminRole(members: SiteMember[]) {
    return new Promise<User[]>((resolv) => {
      this.socketRef.emit('remove site admin role', members, (res: User[]) => resolv(res));
    });
  }
  public emitActivateAutoDelete(members: SiteMember[]) {
    return new Promise<SiteMember[]>((resolv) => {
      this.socketRef.emit('activate auto delete', members, (res: SiteMember[]) => resolv(res));
    });
  }
  public emitDeactivateAutoDelete(members: SiteMember[]) {
    return new Promise<SiteMember[]>((resolv) => {
      this.socketRef.emit('deactivate auto delete', members, (res: SiteMember[]) => resolv(res));
    });
  }
  public emitGetLoginProviders() {
    return new Promise<LoginProvider[]>((resolv) => {
      this.socketRef.emit('get login providers', (res: LoginProvider[]) => {
        resolv(res);
      });
    });
  }
  public emitUpdateLoginProviderName(id: string, name: string) {
    return new Promise<LoginProvider>((resolv) => {
      this.socketRef.emit('update login provider name', id, name, (res: LoginProvider) => {
        resolv(res);
      });
    });
  }
  public emitUpdateLoginProviderEnable(id: string, enable: boolean) {
    return new Promise<LoginProvider>((resolv) => {
      this.socketRef.emit('update login provider enable', id, enable, (res: LoginProvider) => {
        resolv(res);
      });
    });
  }
  public emitUpdateLoginProviderAllowAccountCreationInAdvance(id: string, allow: boolean) {
    return new Promise<LoginProvider>((resolv) => {
      this.socketRef.emit(
        'update login provider allow account creation in advance',
        id,
        allow,
        (res: LoginProvider) => {
          resolv(res);
        },
      );
    });
  }
  public emitGetMyFavoriteRooms() {
    this.socketRef.emit('get my favorite rooms');
  }
  public emitUpdateMyFavoriteRooms(favoriteRoomIds: string[]) {
    this.socketRef.emit('update my favorite rooms', { favoriteRoomIds });
  }

  public emitTest() {
    this.socketRef.emit('exit test');
  }
  public emitUpdateSortOrder(providers: LoginProvider[]) {
    const query = providers.map((p) => ({ id: p.id, order: p.sortOrder }));
    this.socketRef.emit('update login provider sort order', query);
  }
  public emitUpdateFavoriteMessageHistory(messageId: string, isFavorite: boolean) {
    this.socketRef.emit('update favorite message history', messageId, isFavorite);
  }
  public emitDeleteMessageHistory(messageId: string) {
    this.socketRef.emit('delete message history', messageId);
  }
  public emitUpdateUserProperties(properties: Partial<UserProperties>): Promise<UserProperties> {
    return new Promise<UserProperties>((resolve, reject) => {
      this.socketRef.emit('update user properties', properties, (pp: UserProperties) => resolve(pp));
    });
  }
  public emitQuestionnaireResult(result: QuestionnarieResult) {
    return new Promise<QuestionnarieResult | string>((resolve, reject) => {
      this.socketRef.emit('questionnarie', result, (callbackResult: QuestionnarieResult | string) =>
        resolve(callbackResult),
      );
    });
  }
  public emitUpdateCampaign(campaign: Campaign) {
    return new Promise<Campaign>((resolve, reject) => {
      this.socketRef.emit('update campaign', campaign, (c: Campaign) => {
        resolve(c);
      });
    });
  }
  public emitDeleteCampaigns(campaigns: Campaign[], onError?: (message: string) => void) {
    return new Promise<string | {}>((resolve, reject) => {
      this.socketRef.emit('delete campaign', campaigns, (message?: string) => {
        if (message) {
          onError && onError(message);
          console.error(message);
          reject(message);
        } else {
          resolve({});
        }
      });
    });
  }
  public emitGetCampaigns() {
    return new Promise<Campaign[]>((resolve, reject) => {
      this.socketRef.emit('request campaigns', (campaigns: Campaign[]) => {
        resolve(campaigns);
      });
    });
  }

  public emitRegisterCampaign(campaign: Campaign) {
    return new Promise<Campaign>((resolve, reject) => {
      this.socketRef.emit('register campaign', campaign, (campaign: Campaign) => {
        resolve(campaign);
      });
    });
  }

  public emitGetRoomLog() {
    return new Promise<RoomActivityLogs>((resolv, reject) => {
      this.socketRef.emit('get room logs', (logs: RoomActivityLogs) => resolv(logs));
    });
  }
}
